import { LICENSE_BANNER_DISMISS_KEY } from "../components/LicenseTermBanner.jsx";
// src/auth/sessionGuard.js
//
// Cross-user contamination guard for localStorage-backed Zustand stores.
//
// Problem: localStorage is per-origin, not per-user. If user A logs out
// and user B logs in on the same browser profile, all persisted Zustand
// stores would hand user B user A's state.
//
// Solution: stamp the KC `sub` into a dedicated localStorage key at
// login. On subsequent loads, compare stored sub vs. current sub. If
// they differ, wipe every localStorage key under the app's prefixes —
// except an explicit allowlist — before the stores hydrate. The wipe is
// deny-by-default on purpose; see keysToWipe() below.
//
// Same user logging out and back in preserves all their state —
// "pick up where you left off" on reconnect. Different user logging
// in triggers a full wipe of cross-user-sensitive state.
//
// Must run BEFORE any persist-backed store is imported/initialized —
// import from main.jsx right after `window.__keycloak = keycloak`
// and before the first React render.

import {
  SWEPT_KEY_PREFIXES,
  SWEPT_EXTRA_KEYS,
  PRESERVED_ACROSS_USERS,
} from "./persistKeys";

const USER_STAMP_KEY = "aiprime.session.userSub";

// Returns every localStorage key that must not survive a different-user login.
//
// THIS IS DELIBERATELY OVER-BROAD, and reading it as a bug is the mistake to
// avoid. It sweeps by prefix and preserves an explicit allowlist, rather than
// deleting a named list of keys, because the named list failed silently twice:
// once when a key was never added to it, and once when a key was removed from
// it along with its store, leaving copies in already-deployed browsers that
// nothing would ever clear. See the long note in persistKeys.js.
//
// Wrong in this direction: a key we forgot to allowlist gets wiped, a user
// loses a display preference, and someone reports it.
// Wrong in the other direction: one user's state is handed to the next user on
// a shared browser profile, and nobody finds out.
//
// Do not narrow this to an exact-key list.
function keysToWipe() {
  const preserved = new Set(PRESERVED_ACROSS_USERS);
  const doomed = [];
  // Collect before removing: removeItem() re-indexes localStorage, so deleting
  // inside the loop skips entries.
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key || preserved.has(key)) continue;
    if (SWEPT_KEY_PREFIXES.some((p) => key.startsWith(p)) || SWEPT_EXTRA_KEYS.includes(key))
      doomed.push(key);
  }
  return doomed;
}

// Returns `true` when a DIFFERENT user was detected (and app-scope persist
// keys were wiped), `false` otherwise. Callers use this to also reset the
// IN-MEMORY state of the persist-backed stores: wiping localStorage here is
// not enough, because those stores hydrate at module-import time — which runs
// before this function does — so their in-memory copy still holds the previous
// user's values (e.g. activeOrgId), which would otherwise leak into the
// X-Active-Org / x-aiprime-project headers on the first authenticated fetch.
export function enforceSessionBoundary() {
  const kc = window.__keycloak;
  const currentSub = kc?.tokenParsed?.sub;

  if (!currentSub) return false; // not authed yet, nothing to guard

  const storedSub = localStorage.getItem(USER_STAMP_KEY);
  const userChanged = !!storedSub && storedSub !== currentSub;

  if (userChanged) {
    // Different user on the same browser profile. Wipe app state so
    // the new user starts clean.
    const doomed = keysToWipe();
    console.info("[session-guard] user changed; clearing app state", {
      from: storedSub,
      to: currentSub,
      cleared: doomed,
    });
    for (const key of doomed) {
      localStorage.removeItem(key);
    }
  }

  // Stamp the current user for next time. Must persist across logout
  // so a different-user login can still detect the change.
  localStorage.setItem(USER_STAMP_KEY, currentSub);

  return userChanged;
}

/**
 * Called from the logout handler. Intentionally a no-op for app-scope
 * state — preserves last-session, active org, and active project so
 * the same user returning sees their workspace restored. The cross-
 * user guard above wipes these at the next login if a *different*
 * user logs in.
 *
 * Kept as an exported function so AuthContext's logout flow keeps its
 * current shape. If you add auth-local caches later (e.g. a
 * transient upload draft store) that SHOULD wipe on logout, add them
 * to a new list here.
 */
export function clearSessionState() {
  // Intentionally empty for app-scope localStorage. See note above.
  //
  // sessionStorage is a different question and DOES get cleared. The licence
  // banner records its dismissal there — per tab, dying with the tab — which
  // is why it is not in persistKeys and not governed by the cross-user guard
  // above. But a logout inside a live tab does not end that tab, so without
  // this the next user to log in there inherits a banner the previous user
  // dismissed, about an org that may not even be theirs.
  //
  // Prefix sweep rather than one fixed key: the banner keys its dismissal by
  // tier and state (aiprime.licenseBanner.dismissed:<tier>:<state>), so there
  // is no single key to remove.
  //
  // IMPERSONATION IS THE SECOND PREFIX, AND IT HAD TO BE ADDED RATHER THAN
  // INHERITED. `debug.as_org` moved from localStorage to sessionStorage to get
  // per-tab scope. In localStorage it was covered by SWEPT_KEY_PREFIXES, which
  // enforceSessionBoundary applies — but that function iterates localStorage
  // ONLY, so the move silently left the key outside every sweep. Without this,
  // a platform admin could log out in a tab and hand the next platform admin
  // their impersonation target: not a data leak, since the server still gates
  // the header on the role, but a wrong acting org that nobody chose.
  try {
    const doomed = [];
    for (let i = 0; i < window.sessionStorage.length; i++) {
      const k = window.sessionStorage.key(i);
      if (k && (k.startsWith(LICENSE_BANNER_DISMISS_KEY) || k.startsWith("debug."))) doomed.push(k);
    }
    for (const k of doomed) window.sessionStorage.removeItem(k);
  } catch {
    // Private mode or a blocked store — nothing to clear, nothing to report.
  }
}